// Isolamento de dados por scope (provider + profile).
//
// Princípio fundamental: **cada (provider, perfil) tem seu próprio scope
// nas keys de TODAS as camadas de cache local**. Trocar de scope NUNCA
// apaga dados — apenas troca qual chave está sendo lida/gravada. Cada
// scope mantém seus dados indefinidamente, coexistindo no mesmo browser.
//
// O que cada transição faz:
//
// **Trocar de provider** (login com código novo, logout+login, código
//  diferente): React Query é zerado (queryKeys nem sempre carregam scope
//  ainda — refactor incremental). Snapshot, IDB local-db, vod-info-store,
//  series-info-store **PERMANECEM** — cada provider tem seu scope nas
//  keys. Voltar pro provider antigo reusa tudo (zero re-download).
//
// **Trocar de perfil** dentro do mesmo provider: idem — RQ zerado, NADA
//  apagado. Perfis coexistem; cada um grava seu favorito/posição/settings
//  sob seu scope. Trocar é só mudar a "lente" que olha o IDB.
//
// **Logout completo** (sair da conta, não trocar): RQ zerado. Caches
//  locais não são apagados — se o user voltar a logar com o mesmo provider,
//  encontra seus dados intactos. Limpeza profunda é via "Limpar dados"
//  manual (hardResetLocalDb), separado.
//
// Idempotente. Chamado pelo AppProviders sempre que `scopeKey` muda.

import type { QueryClient } from '@tanstack/react-query'

/**
 * Limpa cache do React Query em transição de scope. NÃO toca em IDB
 * (snapshot, local-db, info stores) — cada scope mantém seus dados
 * sob suas próprias chaves, e voltar reusa tudo.
 *
 * Por que zerar RQ: a maioria das queryKeys ainda não carrega scope
 * (refactor incremental usando `useScopedQueryKey`). Enquanto não
 * estiver 100%, zerar é a única forma de garantir que dados do scope
 * antigo não vazem pro novo. Custo: cache em memória da sessão atual
 * é perdido — caches PERSISTIDOS (IDB) reusam normalmente.
 *
 * Quando todas queryKeys forem scopadas, este zerar pode virar opcional
 * (RQ trataria scope-A e scope-B como queries diferentes).
 */
export function clearScopedData(queryClient: QueryClient): void {
  queryClient.cancelQueries()
  queryClient.clear()
}

/** Scope completo (provider+profile) derivado do store — o mesmo de `currentScope()`
 *  (scope.ts). `_main` quando o usuário logou mas ainda não escolheu perfil. */
export function fullScopeKey(s: {
  providerCode: string | null
  direct: { listName: string } | null
  activeProfileId: string | null
}): string | null {
  let provider: string | null = null
  if (s.providerCode) provider = `p:${s.providerCode}`
  else if (s.direct) provider = `d:${s.direct.listName}`
  if (!provider) return null
  return `${provider}::pf:${s.activeProfileId ?? '_main'}`
}

/**
 * Liga o zerar do React Query à troca de scope, DENTRO do `set()` do store.
 *
 * ── Por que síncrono (10/10/2026) ──
 * Isto era um `useEffect([scopeKey])` no AppProviders, e efeito roda DEPOIS do
 * render. Nesse render, a tela que continua montada (a Home do mobile atravessa a
 * adoção do perfil sem desmontar) já tinha trocado a queryKey e criado a query do scope
 * NOVO; o `clear()` do efeito apagava do cache justamente essa query, com o observer
 * ainda pendurado nela. Daí nenhum `invalidateQueries` a alcançava: o histórico que o
 * pull trazia da TV ficava no IDB e o "Continuar" só aparecia depois de recarregar.
 * Na assinatura do store o zerar acontece ANTES de o React renderizar o scope novo: as
 * queries do scope novo nascem depois do zerar e ficam no cache.
 *
 * Mesma regra de antes: só zera quando havia um scope (o 1º scope depois de "deslogado"
 * não zera nada). Devolve o `unsubscribe`.
 */
export function bindScopeIsolation(
  queryClient: QueryClient,
  store: {
    getState: () => SessionLike
    subscribe: (listener: (s: SessionLike) => void) => () => void
  },
  log: (msg: string) => void = () => {},
): () => void {
  const keyOf = (s: SessionLike): string | null =>
    fullScopeKey({
      providerCode: s.providerCode,
      direct: s.direct ? { listName: s.direct.listName } : null,
      activeProfileId: s.activeProfileId,
    })
  let last = keyOf(store.getState())
  return store.subscribe((s) => {
    const next = keyOf(s)
    if (next === last) return
    if (last !== null) {
      log(`[scope-isolation] scope mudou ${last} → ${next} — zerando RQ`)
      clearScopedData(queryClient)
    }
    last = next
  })
}

interface SessionLike {
  providerCode: string | null
  direct: { listName: string } | null
  activeProfileId: string | null
}
